import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { Kicker, Reveal } from "../components/motion";

const IMGS = [
  "https://static.prod-images.emergentagent.com/jobs/60fe7cfd-579c-48af-b4a9-bdfd3f1b809e/images/355441be1ebd0b9963b26f9dda7afcf0727c4f6d557847b1146765cb214a0f3c.png",
  "https://static.prod-images.emergentagent.com/jobs/60fe7cfd-579c-48af-b4a9-bdfd3f1b809e/images/30e0299e9eca797787de29a57773e059f808d7056f12a2c880e2a7620659ff46.png",
  "https://static.prod-images.emergentagent.com/jobs/60fe7cfd-579c-48af-b4a9-bdfd3f1b809e/images/f1ce41d950ea9b7b0e0a636268d96e34369ec05d9cfb799269390c203763260d.png",
];

function ProgramBlock({ idx, k, reverse }) {
  const { t } = useTranslation();
  const includes = t(`programs.${k}.includes`, { returnObjects: true });

  return (
    <section className={`py-20 lg:py-28 ${idx % 2 ? "surface-2" : "surface-1"}`} data-testid={`programs-block-${k}`}>
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
          <Kicker>{t(`programs.${k}.label`)}</Kicker>
          <h2 className="font-display text-4xl lg:text-6xl mt-5 leading-[1.02] text-ink">{t(`programs.${k}.title`)}</h2>
          <p className="mt-6 text-lg text-slate max-w-2xl leading-relaxed">{t(`programs.${k}.body`)}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <div className="overline text-gold">{t(`programs.${k}.includes_title`)}</div>
              <ul className="mt-4 space-y-3">
                {Array.isArray(includes) && includes.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink/85">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-forest text-white flex items-center justify-center flex-shrink-0"><Check className="h-3 w-3" /></span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="ltr:border-l rtl:border-r border-line ltr:pl-8 rtl:pr-8">
              <div className="overline text-gold">{t(`programs.${k}.goal_label`)}</div>
              <p className="mt-4 font-display text-xl lg:text-2xl leading-snug text-ink">{t(`programs.${k}.goal`)}</p>
            </div>
          </div>
        </Reveal>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}
        >
          <div className="paper aspect-[4/5] overflow-hidden relative">
            <img src={IMGS[idx]} alt="Program visual" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/40 to-transparent" />
            <span className="absolute top-5 ltr:left-5 rtl:right-5 pill !bg-white/90">{t(`home.featured.tag${idx + 1}`)}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function Programs() {
  const { t } = useTranslation();
  return (
    <div data-testid="page-programs">
      <PageHero testId="programs-hero" kicker={t("programs.kicker")} title={t("programs.headline")} lead={t("programs.lead")} />
      <ProgramBlock idx={0} k="p1" />
      <ProgramBlock idx={1} k="p2" reverse />
      <ProgramBlock idx={2} k="p3" />

      <section className="panel-forest" data-testid="programs-footer-note">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 py-16 lg:py-20 flex flex-col lg:flex-row items-start lg:items-center gap-8 justify-between">
          <p className="font-display text-2xl lg:text-3xl max-w-3xl text-white leading-tight">{t("programs.footer_note")}</p>
          <Link to="/join" data-testid="programs-cta-join" className="btn-gold">
            {t("common.apply")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
}
